import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Tournament {
  id: string;
  name: string;
  group_id: string;
  proposed_date: string;
  start_time: string;
  club_id: string | null;
  status: string;
  groups: {
    name: string;
  } | null;
  padel_clubs?: {
    id: string;
    club_name: string;
    street_address?: string;
  } | null;
}

interface UpcomingTournamentsData {
  participating: Tournament[];
  available: Tournament[];
}

export function useUpcomingTournaments() {
  const { user } = useAuth();

  return useQuery<UpcomingTournamentsData>({
    queryKey: ['upcoming-tournaments', user?.id],
    queryFn: async () => {
      if (!user) return { participating: [], available: [] };

      // 1. Get groups where user is a member
      const { data: userGroups } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', user.id);

      const groupIds = userGroups?.map(g => g.group_id) || [];

      if (groupIds.length === 0) {
        return { participating: [], available: [] };
      }

      // 2. Get upcoming tournaments from these groups
      const { data: tournaments, error } = await supabase
        .from('tournaments')
        .select('id, name, group_id, proposed_date, start_time, club_id, status, groups(name), padel_clubs(id, club_name, street_address)')
        .in('group_id', groupIds)
        .in('status', ['open', 'registration_closed', 'in_progress'])
        .gte('proposed_date', (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); })())
        .order('proposed_date', { ascending: true })
        .limit(10);

      if (error) throw error;

      if (!tournaments || tournaments.length === 0) {
        return { participating: [], available: [] };
      }

      // 3. Get user's tournament registrations
      const tournamentIds = tournaments.map(t => t.id);
      
      const { data: userRegistrations } = await supabase
        .from('tournament_registrations')
        .select('tournament_id')
        .eq('user_id', user.id)
        .in('tournament_id', tournamentIds);

      const participatingTournamentIds = new Set(
        userRegistrations?.map(r => r.tournament_id) || []
      );

      // 4. Separate tournaments into participating vs available
      const participating = tournaments.filter(t => 
        participatingTournamentIds.has(t.id)
      );

      const available = tournaments.filter(t => 
        !participatingTournamentIds.has(t.id) && t.status === 'open'
      );

      return { participating, available };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes — tournaments don't change frequently
  });
}
